import React from 'react'
import { Badge } from '../ui/badge'
import { cn } from '../../lib/utils'
import { ArrowRightLeft, Check, Download, Flag, AlertTriangle } from 'lucide-react'
import { Tooltip, TooltipContent, TooltipTrigger } from '../ui/tooltip'
import { SwapStatus, DepositStatus } from '../../types/transactions'
import { formatDisplayBps } from '../../utils/numbers'

const pendingFilter = [
  SwapStatus.Requested,
  SwapStatus.Approved,
  SwapStatus.TradeExecuting,
  SwapStatus.TradeCompleted,
  SwapStatus.WithdrawalInitiated,
  SwapStatus.WithdrawalCompleted,
  SwapStatus.PushOutInitiated,
  DepositStatus.Initiated,
  DepositStatus.Approved,
  DepositStatus.Rejected,
  DepositStatus.LpTransferInitiated,
]

const completedFilter = [
  SwapStatus.Rejected,
  SwapStatus.TradeFailed,
  SwapStatus.WithdrawalFailed,
  SwapStatus.PushOutFailed,
  SwapStatus.PushOutCompleted,
  SwapStatus.Completed,
  SwapStatus.Cancelled,
  DepositStatus.LpTransferCompleted,
  DepositStatus.Completed,
  DepositStatus.LpTransferFailed,
  DepositStatus.RejectedCompleted,
]

const failedFilter = [
  SwapStatus.Rejected,
  SwapStatus.TradeFailed,
  SwapStatus.WithdrawalFailed,
  SwapStatus.PushOutFailed,
  SwapStatus.Cancelled,
  DepositStatus.LpTransferFailed,
  DepositStatus.Rejected,
  DepositStatus.RejectedCompleted,
]

const disclaimerFilter = [
  SwapStatus.TradeFailed,
  SwapStatus.WithdrawalFailed,
  SwapStatus.PushOutFailed,
  DepositStatus.LpTransferFailed,
]

const awaitingFilter = [DepositStatus.Rejected]

const estimatedFilter = [
  SwapStatus.Requested,
  SwapStatus.Approved,
  SwapStatus.Rejected,
  SwapStatus.TradeExecuting,
  SwapStatus.TradeFailed,
]

const getStatusColor = (status: string, type: string) => {
  if (pendingFilter.includes(status as SwapStatus | DepositStatus)) {
    if (status === SwapStatus.Rejected && type === 'TRADE') {
      return 'bg-destructive/5 text-destructive border border-red-500'
    }
    return 'bg-main-orange-muted text-main-orange border border-main-orange'
  }
  if (completedFilter.includes(status as SwapStatus | DepositStatus)) {
    if (failedFilter.includes(status as SwapStatus | DepositStatus)) {
      return 'bg-destructive/5 text-destructive border border-red-500'
    }
    return 'bg-main-green-muted text-main-green border border-main-green'
  }
}

const getStatusDisplayText = (status: string, type: string) => {
  switch (status) {
    case SwapStatus.Requested:
    case DepositStatus.Initiated:
      return 'Awaiting Approval'
    case SwapStatus.Rejected:
    case DepositStatus.Rejected:
      return 'Rejected'
    case DepositStatus.RejectedCompleted:
      return 'Reject Completed'
    case DepositStatus.Approved:
    case SwapStatus.Approved:
      return type === 'TRADE' ? 'Executing Swap' : 'Sending to LP'
    case SwapStatus.TradeExecuting:
      return 'Executing Swap'
    case DepositStatus.LpTransferInitiated:
      return 'Sending to LP'
    case SwapStatus.Cancelled:
      return 'Cancelled'
    case SwapStatus.TradeCompleted:
    case SwapStatus.WithdrawalInitiated:
    case SwapStatus.WithdrawalCompleted:
    case SwapStatus.PushOutInitiated:
      return 'Executing Withdrawal'
    case SwapStatus.TradeFailed:
    case SwapStatus.WithdrawalFailed:
    case SwapStatus.PushOutFailed:
    case DepositStatus.LpTransferFailed:
      return 'Failed'
    case SwapStatus.PushOutCompleted:
    case SwapStatus.Completed:
    case DepositStatus.LpTransferCompleted:
    case DepositStatus.Completed:
      return 'Completed'
  }
  return status
}

const getTypeColor = (type: string) => {
  switch (type) {
    case 'TRADE':
      return 'bg-main-purple/10 text-main-purple border border-main-purple'
    case 'DEPOSIT':
      return 'bg-blue-100 text-blue-700 border border-blue-700'
    default:
      return 'border-gray-700 text-gray-700'
  }
}

const getCompanyTypeText = (tradeType: string) => {
  switch (tradeType) {
    case 'CRYPTO':
      return 'Crypto'
    case 'CRYPTO_AND_FIAT':
      return 'Crypto & FIAT'
    case 'DEACTIVATED':
      return 'Deactivated'
    default:
      return tradeType
  }
}

const getCompanyTypeColor = (tradeType: string) => {
  switch (tradeType) {
    case 'CRYPTO':
    case 'CRYPTO_AND_FIAT':
      return 'border-main text-main'
    case 'DEACTIVATED':
      return 'border-red-700 text-red-700'
    default:
      return 'border-gray-700 text-gray-700'
  }
}

const getGroupStatusColor = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'main_outline'
    case 'DEACTIVATED':
      return 'main_destructive'
    default:
      return 'default'
  }
}

const getGroupStatusText = (status: string) => {
  switch (status) {
    case 'ACTIVE':
      return 'Active'
    case 'DEACTIVATED':
      return 'Deactivated'
    default:
      return status
  }
}

const getUserStatusColor = (isWhitelisted: boolean, isActive: boolean) => {
  if (!isActive) return 'bg-red-100 text-red-700'
  if (isWhitelisted) return 'bg-green-100 text-green-700'
  return 'bg-yellow-100 text-yellow-700'
}

const getUserStatusText = (isWhitelisted: boolean, isTrader: boolean, isActive: boolean) => {
  if (!isActive) return 'Deactivated'
  if (isWhitelisted && isTrader) return 'Authorized'
  return 'Active'
}

const getUserRoleText = (role: string) => {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'Super Admin'
    case 'GROUP_OWNER':
      return 'Group Owner'
    case 'TRADER':
      return 'Trader'
    case 'COMPLIANCE':
      return 'Compliance'
    default:
      return role
  }
}

const getUserRoleColor = (role: string) => {
  switch (role) {
    case 'SUPER_ADMIN':
      return 'bg-purple-100 text-purple-700'
    case 'GROUP_OWNER':
      return 'bg-blue-100 text-blue-700'
    case 'TRADER':
      return 'bg-gray-100 text-gray-700'
    case 'COMPLIANCE':
      return 'bg-yellow-100 text-yellow-700'
    default:
      return 'bg-gray-100 text-gray-700'
  }
}

function DisclaimerIcon({
  showDisclaimer,
  parentRef,
}: {
  showDisclaimer: boolean
  parentRef?: React.RefObject<HTMLElement | null>
}) {
  const [isOpen, setIsOpen] = React.useState(false)

  React.useEffect(() => {
    if (!showDisclaimer || !parentRef?.current) return

    const parentElement = parentRef.current

    const handleMouseEnter = () => setIsOpen(true)
    const handleMouseLeave = () => setIsOpen(false)
    const handleClick = () => setIsOpen((prev) => !prev)

    // Add event listeners
    parentElement.addEventListener('mouseenter', handleMouseEnter)
    parentElement.addEventListener('mouseleave', handleMouseLeave)
    parentElement.addEventListener('click', handleClick)

    return () => {
      parentElement.removeEventListener('mouseenter', handleMouseEnter)
      parentElement.removeEventListener('mouseleave', handleMouseLeave)
      parentElement.removeEventListener('click', handleClick)
    }
  }, [showDisclaimer, parentRef])

  if (!showDisclaimer) return null
  return (
    <Tooltip open={isOpen} onOpenChange={setIsOpen}>
      <TooltipTrigger asChild>
        <AlertTriangle className="h-4 w-4 text-amber-500 cursor-help" />
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={8}
        alignOffset={0}
        avoidCollisions={true}
        collisionPadding={16}
        className="max-w-[280px] p-3 text-sm"
      >
        <p className="text-center">
          This transaction requires manual processing. Please contact support to complete this
          transaction.
        </p>
      </TooltipContent>
    </Tooltip>
  )
}

function AwaitingIcon({
  showAwaiting,
  parentRef,
}: {
  showAwaiting: boolean
  parentRef?: React.RefObject<HTMLElement | null>
}) {
  const [isOpen, setIsOpen] = React.useState(false)

  React.useEffect(() => {
    if (!showAwaiting || !parentRef?.current) return

    const parentElement = parentRef.current

    const handleMouseEnter = () => setIsOpen(true)
    const handleMouseLeave = () => setIsOpen(false)
    const handleClick = () => setIsOpen((prev) => !prev)

    // Add event listeners
    parentElement.addEventListener('mouseenter', handleMouseEnter)
    parentElement.addEventListener('mouseleave', handleMouseLeave)
    parentElement.addEventListener('click', handleClick)

    return () => {
      parentElement.removeEventListener('mouseenter', handleMouseEnter)
      parentElement.removeEventListener('mouseleave', handleMouseLeave)
      parentElement.removeEventListener('click', handleClick)
    }
  }, [showAwaiting, parentRef])

  if (!showAwaiting) return null
  return (
    <Tooltip open={isOpen} onOpenChange={setIsOpen}>
      <TooltipTrigger asChild>
        <AlertTriangle className="h-4 w-4 text-amber-500 cursor-help" />
      </TooltipTrigger>
      <TooltipContent
        side="top"
        align="center"
        sideOffset={8}
        alignOffset={0}
        avoidCollisions={true}
        collisionPadding={16}
        className="max-w-[280px] p-3 text-sm"
      >
        <p className="text-center">The funds are being transferred back to the original wallet.</p>
      </TooltipContent>
    </Tooltip>
  )
}

function StatusBadge({ status, type }: { status: string; type: 'TRADE' | 'DEPOSIT' }) {
  const showDisclaimer = disclaimerFilter.includes(status as SwapStatus)
  const showAwaiting = awaitingFilter.includes(status as DepositStatus) && type === 'DEPOSIT'
  const parentRef = React.useRef<HTMLDivElement>(null)

  return (
    <div
      ref={parentRef}
      className={cn(
        'px-3 py-2 rounded text-xs font-medium border flex items-center justify-center gap-2',
        getStatusColor(status, type),
      )}
    >
      <span className="leading-none pt-0.5">{getStatusDisplayText(status, type)}</span>
      <DisclaimerIcon showDisclaimer={showDisclaimer} parentRef={parentRef} />
      <AwaitingIcon showAwaiting={showAwaiting} parentRef={parentRef} />
    </div>
  )
}

function FlaggedBadge({
  flagged,
  className,
  mobileStyling = false,
}: {
  flagged: boolean
  className?: string
  mobileStyling?: boolean
}) {
  if (!flagged) return null
  return (
    <div
      className={cn(
        'px-3 py-2 rounded text-xs font-medium border flex items-center justify-center gap-2',
        'bg-destructive/5 text-destructive border border-destructive',
        className,
      )}
    >
      <Flag className="h-3 w-3" />
      <span className={cn(mobileStyling && 'hidden md:block')}>Flagged</span>
    </div>
  )
}

function FlaggedBadgeIcon() {
  return (
    <div className="border-sm bg-destructive text-white h-7 w-7 flex items-center justify-center rounded-md md:hidden">
      <Flag className="h-4 w-4" />
    </div>
  )
}

function ExternalApiBadge({
  isExternalApi,
  className,
}: {
  isExternalApi?: boolean | null
  className?: string
}) {
  if (!isExternalApi) return null
  return (
    <Badge
      variant="outline"
      className={cn('text-xs text-muted-foreground border-muted-foreground/40', className)}
    >
      External API — not swappable
    </Badge>
  )
}

function TransactionTypeBadge({ type }: { type: string }) {
  return (
    <Badge
      variant="outline"
      className={cn(
        'px-3 py-1 rounded-md text-xs md:text-xs font-medium flex items-center justify-center gap-1 h-[32px] w-[80px]',
        getTypeColor(type),
      )}
    >
      {type === 'TRADE' ? (
        <ArrowRightLeft className="h-3 w-3 shrink-0" />
      ) : (
        <Download className="w-3 h-3 shrink-0" />
      )}
      {type === 'TRADE' ? 'Swap' : 'Deposit'}
    </Badge>
  )
}

function CompanyRoleBadge({ tradeType }: { tradeType?: string }) {
  if (!tradeType) return null

  return (
    <Badge
      variant="outline"
      className={cn(
        'h-[20px] py-1 px-3 text-xs rounded-sm leading-none',
        getCompanyTypeColor(tradeType),
      )}
    >
      {getCompanyTypeText(tradeType)}
    </Badge>
  )
}

function AutoDepositBadge({ autoApprove }: { autoApprove: boolean }) {
  return (
    <Badge variant="highlight" className="h-[23px] py-1 px-3 text-xs rounded-sm leading-none">
      Auto Deposit : {autoApprove ? 'On' : 'Off'}
    </Badge>
  )
}

function ProfileCompanyBadge() {
  return (
    <Badge
      variant="outline"
      className="h-[20px] py-1 px-3 text-xs rounded-sm leading-none border-main text-main"
    >
      Profile Company
    </Badge>
  )
}

function GroupStatusBadge({ status }: { status: string }) {
  return (
    <Badge
      variant={getGroupStatusColor(status)}
      className="h-[20px] py-0 px-1 text-xs rounded-sm leading-none"
    >
      {status === 'ACTIVE' && <Check className="h-4 w-4" />}
      {getGroupStatusText(status)}
    </Badge>
  )
}

type BPSBadgeTone = 'default' | 'group' | 'lp'

function getBPSBadgeClassName(tone: BPSBadgeTone): string {
  const base = 'h-[22px] px-3 text-xs rounded-sm leading-none'
  if (tone === 'group') {
    return `${base} text-red-600 border-red-300`
  }
  if (tone === 'lp') {
    return `${base} text-blue-600 border-blue-300`
  }
  return base
}

function BPSBadge({ bps, tone = 'default' }: { bps: number; tone?: BPSBadgeTone }) {
  return (
    <Badge variant="main_outline" className={getBPSBadgeClassName(tone)}>
      {formatDisplayBps(bps)} BPS
    </Badge>
  )
}

function UserStatusBadge({
  isWhitelisted,
  isTrader,
  isActive,
}: {
  isWhitelisted: boolean
  isTrader: boolean
  isActive: boolean
}) {
  if (isActive && !isTrader) return null
  if (!isWhitelisted && isActive && isTrader) return null

  return (
    <Badge variant="secondary" className={getUserStatusColor(isWhitelisted, isActive)}>
      {getUserStatusText(isWhitelisted, isTrader, isActive)}
    </Badge>
  )
}

function UserRoleBadge({ role }: { role: string }) {
  return (
    <Badge variant="secondary" className={getUserRoleColor(role)}>
      {getUserRoleText(role)}
    </Badge>
  )
}

export {
  StatusBadge,
  FlaggedBadge,
  FlaggedBadgeIcon,
  ExternalApiBadge,
  TransactionTypeBadge,
  CompanyRoleBadge,
  GroupStatusBadge,
  UserStatusBadge,
  UserRoleBadge,
  DisclaimerIcon,
  BPSBadge,
  AutoDepositBadge,
  ProfileCompanyBadge,
  getUserRoleText,
  getStatusDisplayText,
  getStatusColor,
  pendingFilter,
  completedFilter,
  failedFilter,
  disclaimerFilter,
  estimatedFilter,
}
